import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  DEFAULT_THEME,
  logos,
  palettes,
  shapes,
  siteDefaults,
  unverifiedFields,
  type SiteContent,
} from "@hunts/core";

import { api } from "../api";
import { ErrorNote, Loading, PageHeader } from "../components/Page";
import { Tabs } from "../components/Tabs";

interface Photo {
  key: string;
  size: number;
  uploadedAt?: string;
  url: string;
}

interface GalleryItem {
  key: string;
  caption: string;
}

interface SiteStatus {
  lastEditedAt?: string;
  lastPublishedAt?: string;
  pendingChanges: boolean;
  canPublish: boolean;
}

export function Website({ tab }: { tab: string }) {
  return (
    <>
      <PublishBar />
      <Tabs
        base="/website"
        current={tab}
        tabs={[
          { slug: "", label: "Photos" },
          { slug: "copy", label: "Words" },
          { slug: "design", label: "Design" },
        ]}
      />
      {tab === "" && <Photos />}
      {tab === "copy" && <Copy />}
      {tab === "design" && <Design />}
    </>
  );
}

/* --- Publishing ------------------------------------------------------------ */

/**
 * Saved changes, and the one button that puts them on the website.
 *
 * Saving and publishing are separate on purpose. Editing the site is a session
 * -- a few photos, some captions, a rewritten service description -- and
 * rebuilding on every save would mean a dozen deploys to make one set of
 * changes. So saves accumulate, and this says how many are waiting.
 */
function PublishBar() {
  const qc = useQueryClient();

  const status = useQuery({
    queryKey: ["site-status"],
    queryFn: () => api.get<SiteStatus>("site", "status"),
    // A publish takes a couple of minutes in CI. Nothing here polls it -- the
    // button reports that the build started, not that it finished.
    staleTime: 10_000,
  });

  const publish = useMutation({
    mutationFn: () => api.create<SiteStatus>("site/publish", {}),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["site-status"] }),
  });

  const pending = status.data?.pendingChanges ?? false;
  const canPublish = status.data?.canPublish ?? false;

  return (
    <>
      <PageHeader
        title="Website"
        actions={
          <button
            className="btn btn--primary btn--sm"
            disabled={!pending || !canPublish || publish.isPending}
            onClick={() => publish.mutate()}
          >
            {publish.isPending ? "Starting…" : "Publish to website"}
          </button>
        }
      />

      <p className={`publish ${pending ? "publish--pending" : ""}`}>
        {publish.isSuccess ? (
          <>
            <b>Publishing.</b> The site rebuilds and goes live in a couple of minutes.
          </>
        ) : !canPublish ? (
          <>
            <b>Publishing isn't connected yet.</b> Changes save here, but a GitHub
            token has to be set before this button can rebuild the site. Until
            then the site updates on the next code deploy.
          </>
        ) : pending ? (
          <>
            <b>You have unpublished changes.</b> They're saved, but the website
            still shows the previous version until you publish.
          </>
        ) : (
          <>The website is up to date with everything saved here.</>
        )}
      </p>

      {publish.error != null && <ErrorNote error={publish.error} />}
    </>
  );
}

/* --- Words ----------------------------------------------------------------- */

/** Compares by value, so reordering a list counts as a change — because it is. */
function same(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

type Editable = Omit<SiteContent, "services">;

/**
 * The words on the website.
 *
 * Seeded with what the site actually says today -- the committed default, or
 * the published override where one exists -- so this is never a screen of empty
 * boxes you have to guess at.
 *
 * Only fields that differ from the default are saved. That keeps the published
 * document a short list of real differences, and means a default improved in
 * code still reaches the site for anything nobody has overridden.
 */
function Copy() {
  const qc = useQueryClient();

  const published = useQuery({
    queryKey: ["site-content"],
    queryFn: () => api.get<SiteContent>("site", "content"),
  });

  const [draft, setDraft] = useState<SiteContent | null>(null);

  const save = useMutation({
    mutationFn: (content: SiteContent) => api.put<SiteContent>("site/content", content),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["site-content"] });
      void qc.invalidateQueries({ queryKey: ["site-status"] });
    },
  });

  if (published.isLoading) return <Loading />;
  if (published.error) return <ErrorNote error={published.error} />;

  // What the site says right now: the default, overridden by anything published.
  const { services: publishedServices, ...publishedBusiness } = published.data ?? {};
  const { services: defaultServices, ...defaultBusiness } = siteDefaults;

  const current: SiteContent =
    draft ??
    ({
      ...defaultBusiness,
      ...publishedBusiness,
      services: defaultServices.map((s) => publishedServices?.find((p) => p.slug === s.slug) ?? s),
    } as SiteContent);

  const set = <K extends keyof Editable>(key: K, value: Editable[K]) =>
    setDraft({ ...current, [key]: value });

  const setService = (slug: string, patch: Partial<{ title: string; summary: string; points: string[] }>) =>
    setDraft({
      ...current,
      services: (current.services ?? []).map((s) => (s.slug === slug ? { ...s, ...patch } : s)),
    });

  /** Everything that differs from the committed default, and nothing else. */
  const changesFrom = (content: SiteContent): SiteContent => {
    const out: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(content)) {
      if (key === "services") continue;
      if (!same(value, defaultBusiness[key as keyof typeof defaultBusiness])) out[key] = value;
    }
    const services = (content.services ?? []).filter(
      (s) => !same(s, defaultServices.find((d) => d.slug === s.slug)),
    );
    if (services.length > 0) out["services"] = services;
    return out as SiteContent;
  };

  const changes = changesFrom(current);
  const changedCount = Object.keys(changes).length;
  const isUnverified = (field: string) => (unverifiedFields as readonly string[]).includes(field);

  return (
    <>
      <p className="muted" style={{ maxWidth: "62ch" }}>
        The words on the public website. Anything you leave alone keeps its
        current text — only what you actually change is stored, so this stays a
        short list of your edits rather than a second copy of the site.
      </p>

      {save.error != null && <ErrorNote error={save.error} />}

      <div className="form-card">
        <div className="form-grid">
          <Field
            label="Headline"
            help="The first line on the homepage, and what Google shows under the title. Naming the region is deliberate."
            wide
          >
            <input value={current.tagline ?? ""} onChange={(e) => set("tagline", e.target.value)} />
          </Field>

          <Field label="Phone">
            <input value={current.phone ?? ""} onChange={(e) => set("phone", e.target.value)} />
          </Field>

          <Field label="Email">
            <input value={current.email ?? ""} onChange={(e) => set("email", e.target.value)} />
          </Field>

          <Field label="Hours" unverified={isUnverified("hours")} wide>
            <input value={current.hours ?? ""} onChange={(e) => set("hours", e.target.value)} />
          </Field>

          <Field label="Founded" unverified={isUnverified("foundedYear")}>
            <input
              type="number"
              value={current.foundedYear ?? ""}
              onChange={(e) => set("foundedYear", Number(e.target.value) || undefined)}
            />
          </Field>
        </div>
      </div>

      <h2 className="page-subhead">Towns served</h2>
      <p className="muted" style={{ maxWidth: "62ch" }}>
        Each town is listed on the site and given to Google as an area you serve.
        Claiming a town you can't cover is worse than leaving it out.
      </p>
      <div className="form-card">
        <StringList
          values={current.serviceAreas ?? []}
          onChange={(v) => set("serviceAreas", v)}
          placeholder="Town name"
          addLabel="Add a town"
        />
      </div>

      <h2 className="page-subhead">
        Credentials
        <span className="page-subhead__note"> · shown across the homepage</span>
      </h2>
      <p className="muted" style={{ maxWidth: "62ch" }}>
        This is where a facility manager looks to rule you out, so every line has
        to be true of Hunt's itself. Remove anything you can't back up — an empty
        list simply shows nothing.
      </p>
      <div className="form-card">
        <StringList
          values={current.credentials ?? []}
          onChange={(v) => set("credentials", v)}
          placeholder="e.g. Fully insured and bonded"
          addLabel="Add a credential"
        />
      </div>

      <h2 className="page-subhead">Services</h2>
      {(current.services ?? []).map((service) => (
        <div className="form-card" key={service.slug}>
          <div className="form-grid">
            <Field label="Name" wide>
              <input
                value={service.title}
                onChange={(e) => setService(service.slug, { title: e.target.value })}
              />
            </Field>
            <Field label="Description" wide>
              <textarea
                rows={3}
                value={service.summary}
                onChange={(e) => setService(service.slug, { summary: e.target.value })}
              />
            </Field>
          </div>
          <span className="field__label">What's included</span>
          <StringList
            values={service.points}
            onChange={(points) => setService(service.slug, { points })}
            placeholder="One thing you do"
            addLabel="Add a line"
            max={8}
          />
        </div>
      ))}

      <div className="form-card__actions">
        <button
          className="btn btn--primary"
          disabled={draft === null || save.isPending}
          onClick={() => save.mutate(changes)}
        >
          {save.isPending ? "Saving…" : "Save changes"}
        </button>
        {draft !== null && (
          <button className="btn btn--ghost" onClick={() => setDraft(null)} disabled={save.isPending}>
            Discard
          </button>
        )}
        <span className="muted">
          {save.isSuccess && draft === null
            ? "Saved."
            : changedCount === 0
              ? "Everything matches the original wording."
              : `${changedCount} section${changedCount === 1 ? "" : "s"} differ from the original.`}
        </span>
      </div>
    </>
  );
}

function Field({
  label,
  help,
  wide,
  unverified,
  children,
}: {
  label: string;
  help?: string;
  wide?: boolean;
  unverified?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className={`field ${wide ? "field--wide" : ""}`}>
      <span className="field__label">
        {label}
        {unverified && <span className="field__flag" title="Never confirmed — this was a guess">unverified</span>}
      </span>
      {children}
      {help && <span className="field__help">{help}</span>}
    </label>
  );
}

/**
 * A list of short strings, edited in place.
 *
 * Rows rather than a comma-separated box: several of these lists hold values
 * with commas in them, and every one of them is a claim worth seeing on its own
 * line rather than buried in a run-on string.
 */
function StringList({
  values,
  onChange,
  placeholder,
  addLabel,
  max = 30,
}: {
  values: string[];
  onChange: (values: string[]) => void;
  placeholder: string;
  addLabel: string;
  max?: number;
}) {
  return (
    <div className="string-list">
      {values.map((value, i) => (
        <div className="string-list__row" key={i}>
          <input
            value={value}
            placeholder={placeholder}
            onChange={(e) => onChange(values.map((v, j) => (j === i ? e.target.value : v)))}
          />
          <button
            className="btn btn--ghost btn--sm"
            aria-label={`Remove ${value || placeholder}`}
            onClick={() => onChange(values.filter((_, j) => j !== i))}
          >
            Remove
          </button>
        </div>
      ))}
      {values.length < max && (
        <button className="btn btn--ghost btn--sm" onClick={() => onChange([...values, ""])}>
          {addLabel}
        </button>
      )}
    </div>
  );
}

/* --- Design ---------------------------------------------------------------- */

/**
 * Palette, corner style and logo.
 *
 * A short list rather than a colour picker and a font menu. Every palette here
 * has been checked to hold white text on its dark band, which is where a
 * free-form choice goes wrong first -- and a logo the business can actually use
 * at 28px in a header beats one that only works on a screen.
 *
 * Each option previews in its own colours, so the choice is made by looking
 * rather than by reading slugs.
 */
function Design() {
  const qc = useQueryClient();

  const published = useQuery({
    queryKey: ["site-content"],
    queryFn: () => api.get<SiteContent>("site", "content"),
  });

  const [draft, setDraft] = useState<SiteContent["theme"] | null>(null);

  const save = useMutation({
    mutationFn: (theme: SiteContent["theme"]) =>
      api.put<SiteContent>("site/content", { ...(published.data ?? {}), theme }),
    onSuccess: () => {
      setDraft(null);
      void qc.invalidateQueries({ queryKey: ["site-content"] });
      void qc.invalidateQueries({ queryKey: ["site-status"] });
    },
  });

  if (published.isLoading) return <Loading />;
  if (published.error) return <ErrorNote error={published.error} />;

  const current = draft ?? published.data?.theme ?? {};
  const palette = palettes.find((p) => p.slug === (current.palette ?? DEFAULT_THEME.palette))!;
  const set = (patch: Partial<NonNullable<SiteContent["theme"]>>) =>
    setDraft({ ...current, ...patch });

  /** Renders a mark in a given palette, since the SVGs take their colours from CSS. */
  const markStyle = (colour: string, accent: string) =>
    ({ color: colour, ["--accent" as string]: accent }) as React.CSSProperties;

  return (
    <>
      <p className="muted" style={{ maxWidth: "62ch" }}>
        How the website looks. These are the whole set of choices on purpose —
        each palette has been checked so white text stays readable on the dark
        band, which is the first thing a free colour picker gets wrong.
      </p>

      {save.error != null && <ErrorNote error={save.error} />}

      <h2 className="page-subhead">Colours</h2>
      <div className="swatches">
        {palettes.map((option) => (
          <button
            key={option.slug}
            className={`swatch ${option.slug === palette.slug ? "swatch--on" : ""}`}
            aria-pressed={option.slug === palette.slug}
            onClick={() => set({ palette: option.slug })}
          >
            <span className="swatch__bars">
              <span style={{ background: option.tokens.brandDeep }} />
              <span style={{ background: option.tokens.brand }} />
              <span style={{ background: option.tokens.accent }} />
            </span>
            <b>{option.label}</b>
            <span className="swatch__note">{option.note}</span>
          </button>
        ))}
      </div>

      <h2 className="page-subhead">Corners</h2>
      <div className="swatches swatches--compact">
        {shapes.map((option) => (
          <button
            key={option.slug}
            className={`swatch ${option.slug === (current.shape ?? DEFAULT_THEME.shape) ? "swatch--on" : ""}`}
            aria-pressed={option.slug === (current.shape ?? DEFAULT_THEME.shape)}
            onClick={() => set({ shape: option.slug })}
          >
            <span
              className="swatch__shape"
              style={{ borderRadius: option.tokens.radiusLg, background: palette.tokens.brand }}
            />
            <b>{option.label}</b>
            <span className="swatch__note">{option.note}</span>
          </button>
        ))}
      </div>

      <h2 className="page-subhead">Logo</h2>
      <p className="muted" style={{ maxWidth: "62ch" }}>
        The mark only — the business name sits beside it as ordinary text, so it
        stays readable to Google and to a screen reader. Each one takes its
        colours from the palette above.
      </p>
      <div className="swatches swatches--compact">
        {logos.map((option) => (
          <button
            key={option.slug}
            className={`swatch ${option.slug === (current.logo ?? DEFAULT_THEME.logo) ? "swatch--on" : ""}`}
            aria-pressed={option.slug === (current.logo ?? DEFAULT_THEME.logo)}
            onClick={() => set({ logo: option.slug })}
          >
            <span
              className="swatch__logo"
              style={markStyle(palette.tokens.brand, palette.tokens.accent)}
              dangerouslySetInnerHTML={{ __html: option.svg }}
            />
            <b>{option.label}</b>
            <span className="swatch__note">{option.note}</span>
          </button>
        ))}
      </div>

      <div className="form-card__actions">
        <button
          className="btn btn--primary"
          disabled={draft === null || save.isPending}
          onClick={() => save.mutate(current)}
        >
          {save.isPending ? "Saving…" : "Save design"}
        </button>
        {draft !== null && (
          <button className="btn btn--ghost" onClick={() => setDraft(null)} disabled={save.isPending}>
            Discard
          </button>
        )}
        <span className="muted">
          {draft === null ? "Saved." : "Not saved yet."}
        </span>
      </div>
    </>
  );
}

/* --- Photos ---------------------------------------------------------------- */

/** The longest edge we publish. Beyond this is bandwidth nobody sees. */
const MAX_EDGE = 1600;
const JPEG_QUALITY = 0.82;

/**
 * Shrinks a photo in the browser before it is uploaded.
 *
 * A photo straight off a phone is 4000px and several megabytes; the gallery
 * displays it at a few hundred. Downscaling here rather than on the way out
 * means the upload is quick on a phone connection, S3 holds something sensible,
 * and no image-processing dependency or Lambda enters the picture.
 *
 * Uses canvas, which is why the accepted types are the ones canvas can decode.
 */
async function downscale(file: File): Promise<Blob> {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));

  // Already small enough: re-encoding would only lose quality.
  if (scale === 1 && file.size < 1_500_000) return file;

  const canvas = document.createElement("canvas");
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);

  const ctx = canvas.getContext("2d");
  if (!ctx) return file;
  ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, "image/jpeg", JPEG_QUALITY),
  );
  return blob ?? file;
}

function Photos() {
  const qc = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(0);
  const [error, setError] = useState<unknown>(null);
  const [captions, setCaptions] = useState<Record<string, string>>({});

  const photos = useQuery({
    queryKey: ["site-photos"],
    queryFn: () => api.list<Photo>("site/photos"),
  });

  const gallery = useQuery({
    queryKey: ["site-gallery"],
    queryFn: async () => {
      const res = await api.get<{ items?: GalleryItem[] }>("site", "gallery");
      return res.items ?? [];
    },
  });

  /** Caption as edited, else as published, else empty. */
  const captionFor = (key: string) =>
    captions[key] ?? gallery.data?.find((i) => i.key === key)?.caption ?? "";

  const upload = async (files: FileList) => {
    setError(null);
    setUploading(files.length);

    try {
      for (const file of Array.from(files)) {
        const blob = await downscale(file);
        // The presigned PUT is issued for a specific content type, so the upload
        // has to send exactly that.
        const contentType = blob.type || "image/jpeg";
        const { url } = await api.create<{ url: string; key: string }>(
          "site/photos/upload-url",
          { contentType },
        );

        const res = await fetch(url, {
          method: "PUT",
          headers: { "Content-Type": contentType },
          body: blob,
        });
        if (!res.ok) throw new Error(`Upload failed (${res.status})`);
      }
      void qc.invalidateQueries({ queryKey: ["site-photos"] });
    } catch (err) {
      setError(err);
    } finally {
      setUploading(0);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  const remove = useMutation({
    mutationFn: (key: string) => api.remove("site/photos", `?key=${encodeURIComponent(key)}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["site-photos"] }),
  });

  const saveGallery = useMutation({
    mutationFn: () => {
      const items = (photos.data ?? [])
        .filter((p) => captionFor(p.key).trim())
        .map((p) => ({ key: p.key, caption: captionFor(p.key).trim() }));
      return api.put<{ items: GalleryItem[] }>("site/gallery", { items });
    },
    onSuccess: () => {
      setCaptions({});
      void qc.invalidateQueries({ queryKey: ["site-gallery"] });
      void qc.invalidateQueries({ queryKey: ["site-status"] });
    },
  });

  const captioned = (photos.data ?? []).filter((p) => captionFor(p.key).trim()).length;

  return (
    <>
      <p className="muted" style={{ maxWidth: "62ch" }}>
        Photos of finished work for the site's gallery, which currently shows
        placeholder tiles. Give a photo a caption to include it; leave the caption
        blank and it stays here without appearing on the site.
      </p>

      <div className="form-card">
        <label className="field">
          <span className="field__label">Add photos</span>
          <input
            ref={fileInput}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            multiple
            disabled={uploading > 0}
            onChange={(e) => {
              if (e.target.files?.length) void upload(e.target.files);
            }}
          />
          <span className="field__help">
            Straight off a phone is fine — they're shrunk here before uploading, so
            a big photo doesn't mean a slow upload.
          </span>
        </label>
        {uploading > 0 && (
          <p className="muted">
            Uploading {uploading} photo{uploading === 1 ? "" : "s"}…
          </p>
        )}
      </div>

      {error != null && <ErrorNote error={error} />}
      {saveGallery.error != null && <ErrorNote error={saveGallery.error} />}
      {remove.error != null && <ErrorNote error={remove.error} />}

      <h2 className="page-subhead">
        Photos
        {photos.data && photos.data.length > 0 && (
          <span className="page-subhead__note">
            {" "}
            · {captioned} of {photos.data.length} in the gallery
          </span>
        )}
      </h2>

      {photos.isLoading ? (
        <Loading />
      ) : photos.error ? (
        <ErrorNote error={photos.error} />
      ) : (photos.data ?? []).length === 0 ? (
        <p className="muted">No photos yet. Add some above.</p>
      ) : (
        <>
          <ul className="photos">
            {(photos.data ?? []).map((photo) => (
              <li key={photo.key} className="photos__item">
                <img src={photo.url} alt="" loading="lazy" />
                <input
                  placeholder="Caption — required to publish"
                  value={captionFor(photo.key)}
                  onChange={(e) =>
                    setCaptions((prev) => ({ ...prev, [photo.key]: e.target.value }))
                  }
                />
                <button
                  className="btn btn--sm btn--danger"
                  disabled={remove.isPending}
                  onClick={() => {
                    if (window.confirm("Delete this photo?")) remove.mutate(photo.key);
                  }}
                >
                  Delete
                </button>
              </li>
            ))}
          </ul>

          <div className="form-card__actions">
            <button
              className="btn btn--primary"
              disabled={saveGallery.isPending || captioned === 0}
              onClick={() => saveGallery.mutate()}
            >
              {saveGallery.isPending ? "Saving…" : "Save gallery"}
            </button>
          </div>
        </>
      )}
    </>
  );
}
